import { useEffect, useMemo, useState } from "react";
import { CircularProgress, FormHelperText, InputLabel } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HighlightOffOutlined } from "@mui/icons-material";
import { useProposalContext } from "../..";
import { Setting } from "../../../../utils/Setting";
import Images from "../../../../config/images";
import {
  getApiData,
  getAPIProgressData,
  postFormData,
} from "../../../../utils/APIHelper";
import { isArray, isEmpty } from "lodash";
import ImageUploader from "./ImageUploader";
import { createRoomsConfiguration } from "../../../../services/budgetService";
import ProjectDetailsSection from "./ProjectDetailsSection";
import CustomerDetailsSection from "./CustomerDetailsSection";
import ActionBar from "./ActionBar";
import ScopeItems from "./ScopeItems";
import { ReactComponent as LeftArrowIcon } from "../../../../assets/svg/LeftArrowIcon.svg";
import "./summary.css";

const errorObj = {
  typeErr: false,
  typeMsg: "",
  nameErr: false,
  nameMsg: "",
  descriptionErr: false,
  descriptionMsg: "",
  documentErr: false,
  documentMsg: "",
  scopeOfWorkErr: false,
  scopeOfWorkMsg: "",
  customerNameErr: false,
  customerNameMsg: "",
  customerEmailErr: false,
  customerEmailMsg: "",
  clientTypeErr: false,
  clientTypeMsg: "",
  scopeErr: false,
  scopeMsg: "",
  spaceTypeErr: false,
  spaceTypeMsg: "",
  locationErr: false,
  locationMsg: "",
  unitErr: false,
  unitMsg: "",
  unitSizeErr: false,
  unitSizeMsg: "",
  startDateErr: false,
  startDateMsg: "",
  endDateErr: false,
  endDateMsg: "",
};

const MAX_DESCRIPTION_LENGTH = 1000;

const dropdownProjectTypes = ["Villa", "Apartment", "Airbnb"];

const formatApiDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const day = String(d.getDate()).padStart(2, "0");
  return `${months[d.getMonth()]} ${day}, ${d.getFullYear()}`;
};

const formatIsoDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normProjectLabel = (o) =>
  String(o?.label ?? o?.value ?? "").trim().toLowerCase();

const Summary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { proposalDetails, setProposalDetails } = useProposalContext();

  const [projectTypesList, setProjectTypesList] = useState([]);
  const [errObj, setErrObj] = useState(errorObj);
  const [details, setDetails] = useState({
    project_type: "",
    project_type_obj: null,
    space_type: null,
    exp_id: null,
    name: "",
    description: "",
    scope_of_work: "",
    username: "",
    customer_email: "",
    project_image: [],
    location: "",
    client_type: "",
    scope: "",
    unit: "",
    unit_size: "",
    start_date: null,
    end_date: null,
    form_json: null,
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [projectImage, setProjectImage] = useState(null);
  const [descriptionManuallyEdited, setDescriptionManuallyEdited] = useState(false);
  const [scopeItemsData, setScopeItemsData] = useState(null);

  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  useEffect(() => {
    getProjectTypes();
  }, []);

  useEffect(() => {
    if (proposalDetails && proposalDetails?.proposal_id) {
      getDetailsApiCall(proposalDetails?.proposal_id);
    } else {
      setIsDisabled(false);
    }
  }, [proposalDetails?.proposal_id]);

  // Resolve project type object once projectTypes & saved details are loaded
  useEffect(() => {
    if (!projectTypesList?.length) return;
    if (!details.project_type) return;
    if (details.project_type_obj) return;

    const n = (v) => String(v ?? "").trim().toLowerCase();
    const slug = n(details.project_type);
    const jsonType = details?.form_json?.json_type
      ? n(details.form_json.json_type)
      : "";

    const candidates = projectTypesList.filter(
      (o) => n(o.project_type) === slug
    );

    let match = null;
    if (candidates.length === 1) {
      match = candidates[0];
    } else if (candidates.length > 1) {
      if (slug === "commercial") {
        if (jsonType === "office" || jsonType === "industrial") {
          match = candidates.find((c) => n(c.project_label) === "office");
        } else {
          match = candidates.find((c) => n(c.project_label) === "commercial");
        }
        match = match || candidates[0];
      } else {
        match =
          candidates.find((c) =>
            (c.spaces || []).some(
              (s) => n(s.type) === jsonType || n(s.label) === jsonType
            )
          ) ||
          candidates.find((c) => n(c.project_label) === jsonType) ||
          candidates[0];
      }
    } else {
      match = projectTypesList.find((o) => n(o.project_label) === slug) || null;
    }

    if (match) {
      setDetails((prev) => ({
        ...prev,
        project_type_obj: match,
        project_type: match.project_type,
        exp_id: match.exp_id ?? match.id ?? prev.exp_id,
      }));
    }
  }, [projectTypesList, details.project_type, details.form_json]);

  const selectedProjectLabel = details.project_type_obj?.project_label || "";
  const showUnitDropdown = dropdownProjectTypes.includes(selectedProjectLabel);

  const resolvedSpaces = useMemo(() => {
    if (!details.project_type_obj) return [];
    const direct = details.project_type_obj.spaces;
    if (Array.isArray(direct) && direct.length > 0) return direct;
    return [];
  }, [details.project_type_obj]);

  const hasSpaceChoices = resolvedSpaces.length > 0;

  const spaceOptions = useMemo(() => {
    if (!hasSpaceChoices) return [];
    const isOfficeProject = normProjectLabel({ label: selectedProjectLabel }) === "office";
    return resolvedSpaces.map((s) => ({
      label: s.label,
      value: isOfficeProject ? s.label : s.type,
    }));
  }, [resolvedSpaces, hasSpaceChoices, selectedProjectLabel]);

  // Auto-set space_type when no space choices, or restore from saved form_json
  useEffect(() => {
    if (!details.project_type_obj) return;

    if (!hasSpaceChoices) {
      if (details.space_type?.value) return;
      setDetails((prev) => ({
        ...prev,
        space_type: {
          label: prev.project_type_obj.project_label,
          value: prev.project_type_obj.project_type,
        },
        form_json: { ...(prev.form_json || {}), json_type: prev.project_type_obj.project_type },
      }));
      return;
    }

    if (details.space_type?.value) return;

    const savedJsonType = details?.form_json?.json_type;
    if (!savedJsonType) return;

    const n = (v) => String(v ?? "").trim().toLowerCase();
    const jt = n(savedJsonType);
    const isOfficeProject = n(selectedProjectLabel) === "office";
    const match = resolvedSpaces.find(
      (s) => n(s.type) === jt || n(s.label) === jt
    );
    if (match) {
      const spaceValue = isOfficeProject ? match.label : match.type;
      setDetails((prev) => ({
        ...prev,
        space_type: { label: match.label, value: spaceValue },
        form_json: { ...(prev.form_json || {}), json_type: spaceValue },
      }));
    }
  }, [details.project_type_obj, hasSpaceChoices, resolvedSpaces, selectedProjectLabel]);

  // Auto-populate description from scope + name (only for new proposals)
  useEffect(() => {
    if (proposalDetails?.proposal_id) return;
    if (!details.scope || !details.name) return;
    if (descriptionManuallyEdited) return;

    const defaultDescription = `${details.scope} for ${details.name}`;
    setDetails((prev) => {
      if (!prev.description || prev.description.trim() === "") {
        return { ...prev, description: defaultDescription };
      }
      const previousPattern = /^.+ for .+$/;
      if (previousPattern.test(prev.description)) {
        return { ...prev, description: defaultDescription };
      }
      return prev;
    });
  }, [details.scope, details.name, descriptionManuallyEdited, proposalDetails?.proposal_id]);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescriptionManuallyEdited(true);
      setDetails((prev) => ({ ...prev, description: value }));
      setErrObj((prev) => ({ ...prev, descriptionErr: false, descriptionMsg: "" }));
    }
  };

  const handleScopeOfWorkChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDetails((prev) => ({ ...prev, scope_of_work: value }));
      setErrObj((prev) => ({ ...prev, scopeOfWorkErr: false, scopeOfWorkMsg: "" }));
    }
  };

  const handleClose = () => {
    navigate("/projects");
  };

  async function getProjectTypes() {
    try {
      const response = await getApiData(
        `${Setting.endpoints.projectTypes}`,
        "GET",
        {}
      );
      if (response?.success && isArray(response?.data)) {
        setProjectTypesList(response.data);
      }
    } catch (error) {
      console.log("projectTypes error:", error);
    }
  }

  async function saveDetailsApiCall(data) {
    setButtonLoader(true);
    try {
      const proposalIdParam = proposalDetails?.proposal_id
        ? `?proposal_id=${proposalDetails.proposal_id}`
        : "";

      const response = await getAPIProgressData(
        `${Setting.endpoints.createDraftProposal}${proposalIdParam}`,
        "POST",
        data,
        true
      );

      if (response?.success) {
        toast.success("Details Added Successfully");
        setProposalDetails((prevDetails) => ({
          ...prevDetails,
          ...response.data,
        }));

        const projectId =
          response?.data?.id ||
          response?.data?.project_id ||
          response?.data?.proposal_id;
        if (projectId) {
          try {
            const label =
              details.project_type_obj?.project_label || details.project_type || "";
            const useEmptyConfiguration = [
              "Landscape",
              "Commercial",
              "Retail",
            ].includes(label);
            const body = useEmptyConfiguration
              ? {}
              : { configuration: details.unit };
            await createRoomsConfiguration(projectId, body);
          } catch (roomsErr) {
            console.error("Failed to create rooms configuration:", roomsErr);
          }
        }

        navigate(`/project/details/${response?.data?.proposal_id}`);
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.toString());
    } finally {
      setButtonLoader(false);
    }
  }

  async function editDetailsApiCall(data) {
    setButtonLoader(true);
    try {
      const response = await getApiData(
        `api/admin/update-project-details`,
        "PATCH",
        data,
        {
          "Content-Type": "application/json",
          Accept: "application/json",
        }
      );

      if (response?.success) {
        toast.success("Details Updated Successfully");
        navigate(
          `/new-create-proposal/${proposalDetails?.proposal_id}/milestone`
        );
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.toString());
    } finally {
      setButtonLoader(false);
    }
  }

  async function getDetailsApiCall(pid) {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.getProject}?proposal_id=${pid}`,
        "GET",
        {}
      );
      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        const data = response?.data[0];

        let normalizedClientType = data?.client_type || "";
        if (normalizedClientType) {
          const upperClientType = normalizedClientType.toUpperCase();
          if (upperClientType === "INDIVIDUAL" || upperClientType === "BUSINESS") {
            normalizedClientType = upperClientType;
          } else if (normalizedClientType.toLowerCase() === "individual") {
            normalizedClientType = "INDIVIDUAL";
          } else if (normalizedClientType.toLowerCase() === "business") {
            normalizedClientType = "BUSINESS";
          }
        }

        let parsedFormJson = null;
        if (data?.form_json) {
          try {
            parsedFormJson =
              typeof data.form_json === "string"
                ? JSON.parse(data.form_json)
                : data.form_json;
          } catch (e) {
            parsedFormJson = null;
          }
        }

        setDetails((prev) => ({
          ...prev,
          project_type: data?.project_type || "",
          project_type_obj: null,
          space_type: null,
          exp_id: data?.exp_id ?? prev.exp_id,
          name: data?.name || "",
          description: data?.description || "",
          username: data?.username || "",
          customer_email: data?.customer_email || "",
          scope_of_work: data?.scope_of_work || "",
          project_image:
            isArray(data?.project_image) && !isEmpty(data?.project_image)
              ? data?.project_image
              : [],
          location: data?.location || "",
          client_type: normalizedClientType,
          scope: data?.scope || "",
          unit: data?.unit || "",
          unit_size: data?.unit_size || "",
          start_date: data?.start_date ? new Date(data.start_date) : null,
          end_date: data?.end_date ? new Date(data.end_date) : null,
          form_json: parsedFormJson,
        }));

        setProposalDetails(data);
        setIsDisabled(true);
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  // All fields are mandatory
  function buildAllValidations() {
    return [
      {
        field: details.project_type,
        errorKey: "typeErr",
        msgKey: "typeMsg",
        message: "Please select project type",
      },
      {
        field: details.name?.trim(),
        errorKey: "nameErr",
        msgKey: "nameMsg",
        message: "Please enter project name",
      },
      {
        field: hasSpaceChoices ? details.space_type?.value : true,
        errorKey: "spaceTypeErr",
        msgKey: "spaceTypeMsg",
        message: "Please select space type",
      },
      {
        field: details.client_type,
        errorKey: "clientTypeErr",
        msgKey: "clientTypeMsg",
        message: "Please select customer type",
      },
      {
        field: details.location,
        errorKey: "locationErr",
        msgKey: "locationMsg",
        message: "Please select location",
      },
      {
        field: details.scope,
        errorKey: "scopeErr",
        msgKey: "scopeMsg",
        message: "Please select scope",
      },
      {
        field: details.unit,
        errorKey: "unitErr",
        msgKey: "unitMsg",
        message: showUnitDropdown
          ? "Please select configuration"
          : "Please select unit",
      },
      {
        field: details.unit_size,
        errorKey: "unitSizeErr",
        msgKey: "unitSizeMsg",
        message: "Please enter unit size",
      },
      {
        field: details.start_date,
        errorKey: "startDateErr",
        msgKey: "startDateMsg",
        message: "Please select start date",
      },
      {
        field: details.end_date,
        errorKey: "endDateErr",
        msgKey: "endDateMsg",
        message: "Please select end date",
      },
      {
        field: details.description?.trim(),
        errorKey: "descriptionErr",
        msgKey: "descriptionMsg",
        message: "Please enter project description",
      },
      {
        field: details.scope_of_work?.trim(),
        errorKey: "scopeOfWorkErr",
        msgKey: "scopeOfWorkMsg",
        message: "Please enter scope of work",
      },
      {
        field: details.username?.trim(),
        errorKey: "customerNameErr",
        msgKey: "customerNameMsg",
        message: "Please enter customer name",
      },
      {
        field:
          details.customer_email?.trim() &&
          emailRegex.test(details.customer_email),
        errorKey: "customerEmailErr",
        msgKey: "customerEmailMsg",
        message: "Please provide a valid customer email",
      },
    ];
  }

  function validateDateRange(newErrObj) {
    if (
      details.start_date &&
      details.end_date &&
      details.end_date <= details.start_date
    ) {
      newErrObj.endDateErr = true;
      newErrObj.endDateMsg = "End date must be greater than start date";
      return false;
    }
    return true;
  }

  function runValidations() {
    const newErrObj = { ...errObj };
    let valid = true;

    buildAllValidations().forEach(({ field, errorKey, msgKey, message }) => {
      if (!field) {
        valid = false;
        newErrObj[errorKey] = true;
        newErrObj[msgKey] = message;
      } else {
        newErrObj[errorKey] = false;
        newErrObj[msgKey] = "";
      }
    });

    if (!validateDateRange(newErrObj)) valid = false;

    setErrObj(newErrObj);
    if (!valid) window.scrollTo({ top: 0, behavior: "smooth" });
    return valid;
  }

  function handleEdit() {
    if (!runValidations()) return;

    if (proposalDetails?.proposal_id) {
      const editPayload = {
        project_id: proposalDetails?.id,
        data: {
          client_type: details.client_type,
          location: details.location,
          scope: details.scope,
          unit: details.unit,
          unit_size: details.unit_size,
          start_date: formatIsoDate(details.start_date),
          end_date: formatIsoDate(details.end_date),
        },
      };
      editDetailsApiCall(editPayload);
    }
  }

  function handleSave() {
    if (!runValidations()) return;

    let formJson = {};
    if (details.space_type?.value) {
      formJson.json_type = details.space_type.value;
    } else if (details.project_type_obj) {
      formJson.json_type =
        details.project_type_obj.project_type ||
        details.project_type_obj.project_label;
    }

    const projectLabel = details.project_type_obj?.project_label || "";
    if (projectLabel === "Landscape") {
      const inclusions = scopeItemsData?.inclusions;
      if (Array.isArray(inclusions) && inclusions.length > 0) {
        formJson.scope_inclusions = inclusions;
      }
    } else if (
      scopeItemsData &&
      typeof scopeItemsData === "object" &&
      Object.keys(scopeItemsData).length > 0
    ) {
      formJson.scope_items = scopeItemsData;
    }

    const body = {
      name: details.name,
      project_type:
        details.project_type_obj?.project_type || details.project_type,
      description: details.description,
      scope_of_work: details.scope_of_work,
      username: details.username,
      customer_email: details.customer_email,
      exp_id:
        details.project_type_obj?.exp_id ||
        details.project_type_obj?.id ||
        details.exp_id ||
        1,
      client_type: details.client_type,
      unit: details.unit,
      location: details.location,
      scope: details.scope,
      unit_size: details.unit_size,
      start_date: formatApiDate(details.start_date),
      end_date: formatApiDate(details.end_date),
      form_json: Object.keys(formJson).length ? JSON.stringify(formJson) : undefined,
      project_image: projectImage,
    };

    saveDetailsApiCall(body);
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }

    const formData = new FormData();
    formData.append("file_1", file);
    formData.append("type", "project");
    formData.append("project_id", proposalDetails?.id);

    setUploadLoader(true);

    try {
      const response = await postFormData(
        Setting.endpoints.projectFiles,
        formData
      );
      if (response.success) {
        await getDetailsApiCall(proposalDetails?.proposal_id);
        toast.success("File uploaded successfully");
        setUploadLoader(false);
        setErrObj((prev) => ({ ...prev, documentErr: false, documentMsg: "" }));
      } else {
        toast.error("File upload failed");
        setUploadLoader(false);
      }
    } catch (error) {
      toast.error("An error occurred during file upload");
      setUploadLoader(false);
    }
  }

  const handleDeleteImage = async () => {
    setUploadLoader(true);
    try {
      const response = await getApiData(
        `api/project/delete-files/${details?.project_image?.[0]?.id}`,
        "POST",
        {},
        { "Content-Type": "application/json" },
        false
      );
      if (response.success) {
        await getDetailsApiCall(proposalDetails?.proposal_id);
        toast.success(response?.message);
        setUploadLoader(false);
      }
    } catch (error) {
      toast.error(error.toString());
      setUploadLoader(false);
    }
  };

  function displayImagesView() {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          borderRadius: 6,
          marginBottom: 10,
          overflow: "hidden",
        }}
      >
        <img
          style={{
            width: "100%",
            height: 250,
            borderRadius: 6,
            objectFit: "cover",
          }}
          src={details?.project_image?.[0]?.image}
          alt="Primary Image"
        />
        <HighlightOffOutlined
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            cursor: "pointer",
            fontSize: 28,
            color: "red",
            zIndex: 10,
          }}
          onClick={handleDeleteImage}
        />
      </div>
    );
  }

  const isLocked = !!proposalDetails?.proposal_id;

  const projectLabel = details.project_type_obj?.project_label || "";
  const savedScopeItemsForRestore = useMemo(() => {
    if (!projectLabel) return undefined;
    if (projectLabel === "Landscape") {
      const inclusions = details?.form_json?.scope_inclusions;
      if (Array.isArray(inclusions) && inclusions.length > 0) {
        return { inclusions };
      }
      return undefined;
    }
    const items = details?.form_json?.scope_items;
    if (items && typeof items === "object" && Object.keys(items).length > 0) {
      return items;
    }
    return undefined;
  }, [projectLabel, details?.form_json]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 0 16px 0",
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: "#fff",
        }}
      >
        <LeftArrowIcon
          onClick={() => navigate(-1)}
          style={{ cursor: "pointer" }}
        />
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "500",
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            margin: 0,
          }}
        >
          {details?.name ? `${details?.name}` : "Create Project"}
        </h2>
      </div>

      {loader ? (
        <center>
          <CircularProgress style={{ color: "#274BF1" }} size={26} />
        </center>
      ) : (
        <div
          className="summary-stack"
          style={{
            overflow: "auto",
            flex: 1,
            minHeight: 0,
            padding: "16px 4px",
          }}
        >
          {/* ── Image Upload ───────────────────────────── */}
          {/* {proposalDetails?.proposal_id ? (
            <div style={{ paddingTop: 10 }}>
              {uploadLoader ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 220,
                  }}
                >
                  <CircularProgress style={{ color: "#274BF1" }} size={26} />
                </div>
              ) : (
                <>
                  <InputLabel
                    shrink
                    error={errObj.documentErr}
                    style={{ fontSize: "1rem", color: "#202939" }}
                  >
                    Project Image
                  </InputLabel>
                  {details?.project_image?.length > 0 ? (
                    <div style={{ width: "100%" }}>{displayImagesView()}</div>
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        marginBottom: "5px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: 170,
                          border: "1px dashed #9CA3AF",
                          borderRadius: 4,
                          gap: 1,
                        }}
                      >
                        <div style={{ width: "24px", height: "24px" }}>
                          <img src={Images.upload_icon} alt="upload-icon" />
                        </div>
                        <InputLabel>
                          <b>
                            <span
                              style={{ cursor: "pointer", color: "#2563EB" }}
                            >
                              Click to upload Images
                            </span>{" "}
                            or drag and drop{" "}
                          </b>
                        </InputLabel>
                        <InputLabel style={{ fontSize: 12, color: "#6B7280" }}>
                          {"PNG, JPG, (max size 1200*800)"}
                        </InputLabel>
                      </div>
                      <input
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          opacity: 0,
                          width: "100%",
                          cursor: "pointer",
                        }}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                      />
                      <FormHelperText
                        error={errObj.documentErr}
                        style={{ fontFamily: "Inter Tight" }}
                      >
                        {errObj.documentMsg}
                      </FormHelperText>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <ImageUploader
              projectImage={projectImage}
              setProjectImage={setProjectImage}
              errObj={errObj}
            />
          )} */}

          <ProjectDetailsSection
            details={details}
            setDetails={setDetails}
            errObj={errObj}
            setErrObj={setErrObj}
            isDisabled={isDisabled}
            projectTypeOptions={projectTypesList}
            hasSpaceChoices={hasSpaceChoices}
            spaceOptions={spaceOptions}
            showUnitDropdown={showUnitDropdown}
            handleDescriptionChange={handleDescriptionChange}
            handleScopeOfWorkChange={handleScopeOfWorkChange}
            isLocked={isLocked}
          />

          <CustomerDetailsSection
            details={details}
            setDetails={setDetails}
            errObj={errObj}
            setErrObj={setErrObj}
            isDisabled={isDisabled}
          />

          <ScopeItems
            projectTypeLabel={projectLabel}
            disabledView={isLocked}
            savedScopeItems={savedScopeItemsForRestore}
            onChange={(data) => setScopeItemsData(data)}
          />
        </div>
      )}

      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid #E5E7EB",
          backgroundColor: "#fff",
          paddingTop: "8px",
        }}
      >
        <ActionBar
          onCancel={handleClose}
          onSubmit={proposalDetails?.id ? handleEdit : handleSave}
          buttonLoader={buttonLoader}
        />
      </div>
    </div>
  );
};

export default Summary;
